import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import Layout from "../components/Layout";
import { 
    collection, 
    getDocs, 
    query, 
    where, 
    orderBy,
    doc,
    getDoc,
    updateDoc,
    increment,
    runTransaction,
    serverTimestamp
} from "firebase/firestore";
import { getISTDate } from "../utils/dateUtils";
import * as XLSX from "xlsx";
import { generateCnFInvoicePdf } from "../utils/pdfCnFInvoice";
import { supabase } from "../supabase";

const SELLER_INFO = {
    name: "SUPREME ENTERPRISES",
    address1: "NO.24, 24 KHATHA KHATHA NO.227/3/7 NO 227/3/7",
    address2: "GROUND FLOOR, NEAR CHRYSALIS SCHOOL,",
    address3: "HORMAVU AGARA, BANGALORE - 560043",
    gstin: "29AFAPC3951P1Z6",
    state: "Karnataka",
    stateCode: "29"
};

function DistributorInvoiceList() {
    const [userProfile, setUserProfile] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [skuMaster, setSkuMaster] = useState({});

    const loadData = async (profile) => {
        try {
            setLoading(true);
            
            // 1. Load SKU Master for PDF generation fallback if needed
            const skuSnap = await getDocs(collection(db, "skuMaster"));
            const skus = {};
            skuSnap.forEach(d => skus[d.data().sku] = d.data());
            setSkuMaster(skus);

            // 2. Load Invoices (Scoped to Client)
            const q = query(
                collection(db, "distributor_invoices"),
                where("clientId", "==", profile.clientId),
                orderBy("createdAt", "desc")
            );
            const snap = await getDocs(q);
            const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            setInvoices(list);
        } catch (err) {
            console.error(err);
            alert("Error loading invoices: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;
            const snap = await getDoc(doc(db, "users", user.uid));
            if (snap.exists()) {
                const profile = snap.data();
                setUserProfile(profile);
                loadData(profile);
            }
        });
        return () => unsub();
    }, []);

    const exportToExcel = () => {
        if (filteredInvoices.length === 0) return;
        
        const data = filteredInvoices.map(inv => ({
            "Invoice ID": inv.displayOrderId,
            "Distributor": inv.partyName,
            "Date": inv.approvedDateOnly,
            "Total Qty (Bags)": inv.totalQty,
            "Basic Amount": inv.totalAmount - (inv.totalAmount * 0.05), // approx or precise if stored
            "Tax Amount": inv.taxAmount || (inv.totalAmount * 0.05), // based on 5% rule
            "Grand Total": inv.totalAmount,
            "Status": inv.status
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "DistributorInvoices");
        XLSX.writeFile(wb, `Distributor_Invoices_${new Date().toISOString().slice(0,10)}.xlsx`);
    };

    const downloadPdf = async (inv) => {
        alert("Preparing PDF Download...");
        
        // Ensure we have address and GST (pull from partyMaster if missing in invoice)
        let finalOrderData = { ...inv };
        if (!inv.address || !inv.gstno) {
            try {
                const distId = inv.distributorId || inv.partyId;
                if (distId) {
                    const distSnap = await getDoc(doc(db, "partyMaster", distId));
                    if (distSnap.exists()) {
                        const dData = distSnap.data();
                        finalOrderData = { ...inv, address: dData.address, gstno: dData.gstno };
                    }
                }
            } catch (err) {
                console.error("Error fetching distributor details for PDF:", err);
            }
        }

        let hsnSum = inv.hsnSummary;
        let totals = inv.financialSummary;

        // Fallback for legacy records or if data missing
        if (!hsnSum) {
            hsnSum = {};
            Object.entries(inv.items || {}).forEach(([sku, qty]) => {
                const m = skuMaster[sku];
                if (!m) return;
                const hsn = (m.hsn || m.HSN || "24022090").trim();
                if (!hsnSum[hsn]) hsnSum[hsn] = { name: m.Name, qty: 0, basic: 0, gstAmt: 0, breakdown: [] };
                
                const rate = Number(m.distributorBasicRate || m.Basic_Eway || 0);
                const c_B = Number(m.cartonsPerBag) || 1;
                const c_C = Number(m.packetsPerCarton) || 1;
                
                const totalPkt = qty * (c_B * c_C);
                const basic = totalPkt * rate;
                const gst = basic * ((Number(m.gst)||5)/100);
                
                hsnSum[hsn].qty += qty;
                hsnSum[hsn].basic += basic;
                hsnSum[hsn].gstAmt += gst;
                
                if (inv.unitConfigs && inv.unitConfigs[sku]) {
                    hsnSum[hsn].breakdown.push(`${sku}: ${inv.unitConfigs[sku]}`);
                }
            });
        }

        if (!totals) {
            totals = {
                tQty: inv.totalQty,
                tGrand: inv.totalAmount,
            };
        }

        const pdf = generateCnFInvoicePdf(inv.displayOrderId, finalOrderData, hsnSum, totals, SELLER_INFO);
        
        // On-demand upload if missing
        if (!inv.taxInvoicePdfUrl) {
            try {
                const blob = pdf.output("blob");
                const fileName = `Invoice-${inv.displayOrderId}.pdf`;
                const { error } = await supabase.storage.from("invoices").upload(fileName, blob, { contentType: "application/pdf", upsert: true });
                if (!error) {
                    const { data } = supabase.storage.from("invoices").getPublicUrl(fileName);
                    if (data?.publicUrl) {
                        await updateDoc(doc(db, "distributor_invoices", inv.id), { taxInvoicePdfUrl: data.publicUrl });
                    }
                }
            } catch (uerr) { console.error("On-demand Supabase upload failed:", uerr); }
        }

        pdf.save(`${inv.displayOrderId}.pdf`);
    };

    const voidInvoice = async (inv) => {
        if (inv.status === "VOID") return;
        const ok = window.confirm(`CRITICAL: Void Invoice ${inv.displayOrderId}? This will reverse stock for BOTH the Hub and the Distributor.`);
        if (!ok) return;

        try {
            setLoading(true);
            await runTransaction(db, async (transaction) => {
                const invRef = doc(db, "distributor_invoices", inv.id);
                const invSnap = await transaction.get(invRef);
                if (!invSnap.exists()) throw new Error("Invoice not found");
                if (invSnap.data().status === "VOID") throw new Error("Already voided");

                const todayStr = inv.approvedDateOnly || getISTDate();
                
                // 1. Reverse Hub (C&F) Stock: sold (Hub) - qty
                const hubStockRef = doc(db, "cnf_stock_daily", `${inv.clientId}_${todayStr}`);
                const hubInv = {};
                Object.entries(inv.items || {}).forEach(([sku, qty]) => { hubInv[`inventory.${sku}.sold`] = increment(-qty); });
                transaction.update(hubStockRef, hubInv);

                // 2. Reverse Distributor Stock: received (Dist) - qty
                const distId = inv.distributorId || inv.partyId;
                const distStockRef = doc(db, "distributor_stock_daily", `${distId}_${todayStr}`);
                const distInv = {};
                Object.entries(inv.items || {}).forEach(([sku, qty]) => { distInv[`inventory.${sku}.received`] = increment(-qty); });
                transaction.update(distStockRef, distInv);

                // 3. Mark Invoice as VOID
                transaction.update(invRef, { status: "VOID", voidedAt: serverTimestamp(), voidedBy: userProfile.uid });
            });
            alert("Invoice VOIDED and Stock Reversed Successfully.");
            loadData(userProfile);
        } catch (err) {
            console.error("Void failed:", err);
            alert("Void failed: " + err.message);
        } finally {
            setLoading(false);
        }
    };

    const filteredInvoices = invoices.filter(inv => 
        inv.displayOrderId?.toLowerCase().includes(search.toLowerCase()) ||
        inv.partyName?.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <Layout><div style={{padding:100, textAlign:'center'}}>Loading Invoice Register...</div></Layout>;

    return (
        <Layout>
            <div className="layout-content">
                <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>C&F INVOICE REGISTER</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Review and manage historical invoices issued from C&F to Distributors.</p>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                        <button onClick={exportToExcel} className="btn-secondary" style={{ background: "var(--surface)", fontWeight:700 }}>Export Records</button>
                        <button onClick={loadData} className="btn-primary" style={{ padding: '0 24px' }}>Refresh</button>
                    </div>
                </div>

                <div className="premium-card" style={{ padding: 12, marginBottom: 32, background: "var(--surface-hover)" }}>
                    <input 
                        type="text" 
                        className="form-input" 
                        placeholder="Search by Invoice # or Distributor..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ width: '100%', height: 50, border: 'none', background: 'var(--surface)' }}
                    />
                </div>

                <div className="premium-card" style={{ padding: 0, overflow: 'hidden' }}>
                    <table className="enterprise-table">
                        <thead>
                            <tr>
                                <th style={{ paddingLeft: 24 }}>INVOICE ID</th>
                                <th>DISTRIBUTOR</th>
                                <th>DATE</th>
                                <th style={{ textAlign: 'right' }}>QTY (BAGS)</th>
                                <th style={{ textAlign: 'right' }}>TOTAL AMOUNT</th>
                                <th style={{ textAlign: 'center', paddingRight: 24 }}>ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredInvoices.map(inv => (
                                <tr key={inv.id}>
                                    <td style={{ paddingLeft: 24, fontWeight: 800 }}>{inv.displayOrderId}</td>
                                    <td style={{ fontWeight: 700 }}>{inv.partyName}</td>
                                    <td>{inv.approvedDateOnly}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{inv.totalQty?.toFixed(2)}</td>
                                    <td style={{ textAlign: 'right', fontWeight: 900, color: inv.status === 'VOID' ? 'var(--text-muted)' : 'var(--primary)', textDecoration: inv.status === 'VOID' ? 'line-through' : 'none' }}>₹{inv.totalAmount?.toLocaleString()}</td>
                                    <td style={{ textAlign: 'center', paddingRight: 24, display: 'flex', gap: 8, justifyContent: 'center' }}>
                                        <button 
                                            onClick={() => downloadPdf(inv)} 
                                            className="btn-small" 
                                            style={{ background: 'var(--primary)', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor:'pointer' }}
                                        >
                                            PDF
                                        </button>
                                        {userProfile?.role === "superadmin" && inv.status !== "VOID" && (
                                            <button 
                                                onClick={() => voidInvoice(inv)} 
                                                className="btn-small" 
                                                style={{ background: 'var(--error)', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor:'pointer' }}
                                            >
                                                VOID
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {filteredInvoices.length === 0 && (
                                <tr>
                                    <td colSpan="6" style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>No invoices found in record.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </Layout>
    );
}

export default DistributorInvoiceList;
